import { useEffect, useState } from 'react';
import { IModuleConfig } from 'redux-state/layout/types.modules.js';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';
import { predicateBoolean, predicateObject } from 'utilities/isType.js';
import { WithRequiredProperty } from 'utilities/types/helper-types.js';
import { CustomError, UseDataUrlQueryHook } from './dataUrlHookQuery.js';
import { FetchDataReturn, FetchDataReturnOK } from './fetchDataAsync.js';

export const REMOVE_MODULE_NAME = 'remove-module';

const debugDataUrl = true;

export type ConfigWithDataUrl = WithRequiredProperty<IModuleConfig, 'dataUrl'>;

const removeModuleConfig = { name: REMOVE_MODULE_NAME as typeof REMOVE_MODULE_NAME };

export const isQueryDataValid = (
  data: FetchDataReturn<DataHookReturnType> | undefined,
): data is FetchDataReturnOK<DataHookReturnType> => {
  if (data && predicateBoolean(data.responseOK) && data.responseOK) {
    return true;
  }
  return false;
};

export const applyRemoveModule = (
  payload?: DataHookReturnType,
): ConfigWithDataUrl | Record<'name', typeof REMOVE_MODULE_NAME> => {
  /** Currently no standardised so need to handle empty objects and null */
  if (payload === null) {
    return removeModuleConfig;
  }
  if (predicateObject(payload) && Object.keys(payload).length === 0) {
    /** 24/10/2024 Agreed with Antz that if object returns empty literal remove
     * To manually add a remove-module object post release as per DIG-2340
     */
    return removeModuleConfig;
  }
  return payload as ConfigWithDataUrl;
};

type DataHookReturnType = ConfigWithDataUrl | null | Record<string, never> | Record<'name', typeof REMOVE_MODULE_NAME>;

/**
 * Checks if module has a dataUrl prop
 * If it does then fetch and merge and pass new props to fn
 */
export function UseDataUrlHook({
  config,
}: {
  config: ConfigWithDataUrl;
}): [CustomError, ConfigWithDataUrl | undefined] {
  const [error, data] = UseDataUrlQueryHook({ dataUrl: config.dataUrl, name: config.name });
  const [queriedConfig, setQueriedConfig] = useState<ConfigWithDataUrl>();

  useEffect(() => {
    if (isQueryDataValid(data)) {
      /**
       * We have dataUrl data
       * merge new data with config
       */
      const normalisedData = applyRemoveModule(data.jsonData);
      const updatedConfig: ConfigWithDataUrl = { ...config, ...normalisedData };
      if (debugDataUrl) {
        messageLog.info(
          `DataUrl Loaded: ${config.name}`,
          ['newData', data.jsonData],
          ['oldData', config],
          ['mergedData', updatedConfig],
        );
      }
      setQueriedConfig(updatedConfig);
    }
  }, [data, config]);

  return [error, queriedConfig];
}
